import { action, makeObservable, observable, reaction, runInAction } from "mobx";
import { ApiClient, CatalogTreeQueryResult } from "../api";
import { isServer } from "../pages/_app";
import type GenomeStore from "./genome-store";

export const NODE_IDX = 0;
export const NODE_TAX_ID = 1;
export const NODE_NAME = 2;
export const NODE_RANK = 3;
export const NODE_CATALOG_NUMBER = 4;
export const NODE_CHILDREN = 5;
export const NODE_PARENT = 6;

const MAX_NUM_OF_RESULTS = 10;
const MAX_NUM_OF_VISIBLE_NODES = 30;

export type TreeNode = [number, string, string, string, string | null, number[], number | null];
type CatalogTreeData = { nodes: TreeNode[]; tags: string[] };

type CatalogNumberToUuid = { [key: string]: string };
type CatalogNumberToTags = { [key: string]: string[] };
type DerivativeParents = { [key: number]: number };
type CatalogTreeUserData = {
  uuids: CatalogNumberToUuid;
  tags: CatalogNumberToTags;
  derivatives: DerivativeParents;
};

type LoadingState = "need-fetching" | "fetching" | "fetched" | "error";

export default class CatalogTreeStore {
  genomesStore: GenomeStore;
  api: ApiClient;

  @observable nodes = observable.array<TreeNode>([], { deep: false });
  private catalogNumberToUuid: CatalogNumberToUuid = {};
  private numWithUuidsWithin: number[] = [];
  private catalogNumberToTags: CatalogNumberToTags = {};
  private derivativeParents: DerivativeParents = {};
  @observable private initialDataState: LoadingState = "need-fetching";
  @observable private state: LoadingState = "need-fetching";
  @observable private visibleIndexes = observable.set<number>([], { deep: false });
  @observable private resultIndexes = observable.set<number>([], { deep: false });
  @observable private numResultsWithinByIdx = observable.map([], { deep: false });

  constructor(api: ApiClient, genomesStore: GenomeStore) {
    this.genomesStore = genomesStore;
    this.api = api;

    makeObservable(this);

    reaction(
      () => {
        return this.genomesStore.getFetchGenomesPayload();
      },
      () => {
        runInAction(() => {
          this.state = "need-fetching";
        });
      }
    );
  }

  getVisibleIndexes = (): { visibleIndexes: number[]; isLoading: boolean; isError: boolean } => {
    const visibleIndexes = [...this.visibleIndexes];
    switch (this.initialDataState) {
      case "need-fetching":
        this.fetchInitialData();
        return { visibleIndexes, isLoading: true, isError: false };
      case "fetching":
        return { visibleIndexes, isLoading: true, isError: false };
      case "error":
        return { visibleIndexes, isLoading: false, isError: true };
      case "fetched":
        switch (this.state) {
          case "need-fetching":
            this.fetchResults();
            return { visibleIndexes, isLoading: true, isError: false };
          case "fetching":
            return { visibleIndexes, isLoading: true, isError: false };
          case "fetched":
            return { visibleIndexes, isLoading: false, isError: false };
          case "error":
            return { visibleIndexes, isLoading: false, isError: true };
        }
    }
  };

  isInResults = (idx: number): boolean => {
    return this.resultIndexes.has(idx);
  };

  isPartOfCatalog = (idx: number): boolean => {
    return this.nodes[idx][NODE_CATALOG_NUMBER] !== null;
  };

  getNumResultsWithin = (idx: number): number => {
    return this.numResultsWithinByIdx.has(idx) ? this.numResultsWithinByIdx.get(idx) : 0;
  };

  getNumWithUuidsWithin = (idx: number): number => {
    return this.numWithUuidsWithin[idx] ?? 0;
  };

  hasUuid = (idx: number): boolean => {
    return !!this.catalogNumberToUuid[this.nodes[idx][NODE_CATALOG_NUMBER] ?? ""];
  };

  getUuid = (idx: number): string | null => {
    return this.catalogNumberToUuid[this.nodes[idx][NODE_CATALOG_NUMBER] ?? ""] ?? null;
  };

  getTags = (idx: number): string[] => {
    return this.catalogNumberToTags[this.nodes[idx][NODE_CATALOG_NUMBER] ?? ""] ?? [];
  };

  isDerivative = (idx: number): boolean => {
    const derivativeParentIdx = this.derivativeParents[idx] ?? null;
    return derivativeParentIdx !== null;
  };

  getDerivativeParent = (derivativeIdx: number): number | null => {
    return this.derivativeParents[derivativeIdx] ?? null;
  };

  @action fetchResults = async () => {
    runInAction(() => {
      this.state = "fetching";
    });

    const payload = this.genomesStore.getFetchGenomesPayload();
    const res = await this.api.post<CatalogTreeQueryResult>(
      "/genome-portal-api/genomes/catalog-tree-data/user-data",
      payload
    );
    if (res.error === undefined) {
      runInAction(() => {
        this.resultIndexes.replace(res.data.result_indexes);
        this.refreshVisibleIndexes();

        this.numResultsWithinByIdx.clear();
        this.recalculateNumResults(this.visibleIndexes);

        this.state = "fetched";
      });
    } else {
      this.state = "error";
    }
  };

  @action toggleNode = (idx: number) => {
    // Behavior differs depending if node contains nodes with results
    const numResults = this.getNumResultsWithin(idx);

    if (numResults > 0) {
      let numResultsOpened = 0;
      for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
        if (this.visibleIndexes.has(childIdx)) {
          numResultsOpened += this.getNumResultsWithin(childIdx);
          if (this.resultIndexes.has(childIdx)) {
            numResultsOpened++;
          }
        }
      }

      if (numResults === numResultsOpened) {
        this.closeAllChildren(idx);
      } else {
        this.openChildrenWithResults(idx);
      }
    } else {
      let numVisibleChildren = 0;
      for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
        if (this.visibleIndexes.has(childIdx)) {
          numVisibleChildren++;
        }
      }

      if (numVisibleChildren === this.nodes[idx][NODE_CHILDREN].length) {
        this.closeAllChildren(idx);
      } else {
        this.openAllChildren(idx);
      }
    }
  };

  @action closeAllChildren = (idx: number) => {
    for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
      this.hideRecursively(childIdx);
    }
  };

  @action openAllChildren = (idx: number) => {
    const newlyOpenedIndexes = new Set<number>();
    for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
      if (!this.visibleIndexes.has(childIdx)) {
        this.visibleIndexes.add(childIdx);
        newlyOpenedIndexes.add(childIdx);
      }
    }
    this.recalculateNumResults(newlyOpenedIndexes);
  };

  @action openChildrenWithResults = (idx: number) => {
    const newlyOpenedIndexes = new Set<number>();
    for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
      if (this.getNumResultsWithin(childIdx) === 0 && !this.resultIndexes.has(childIdx)) {
        continue;
      }

      if (!this.visibleIndexes.has(childIdx)) {
        this.visibleIndexes.add(childIdx);
        newlyOpenedIndexes.add(childIdx);
      }
    }

    this.recalculateNumResults(newlyOpenedIndexes);
  };

  @action setNewParent = (newRootIdx: number) => {
    if (this.visibleIndexes.size === 0) {
      return;
    }

    const newlyOpenedIndexes = new Set<number>();
    let currentRootIdx = this.visibleIndexes.values().next()!.value as number;
    while (currentRootIdx !== newRootIdx) {
      const parentIdx = this.nodes[currentRootIdx][NODE_PARENT];
      if (parentIdx === null) {
        return;
      }
      currentRootIdx = parentIdx;
      this.visibleIndexes.add(parentIdx);
      newlyOpenedIndexes.add(parentIdx);
    }

    // Make sure new root is the first item
    this.visibleIndexes.delete(newRootIdx);
    this.visibleIndexes.replace([newRootIdx, ...this.visibleIndexes]);
    this.recalculateNumResults(newlyOpenedIndexes);
  };

  private refreshVisibleIndexes = () => {
    this.visibleIndexes.clear();
    if (this.nodes.length === 0 || this.resultIndexes.size === 0) {
      return;
    }

    const visibleIndexes = new Set<number>();
    for (const idx of this.resultIndexes) {
      let current = this.nodes[idx];
      while (true) {
        visibleIndexes.add(current[NODE_IDX]);
        const parentIdx = current[NODE_PARENT];
        if (parentIdx === null) {
          break;
        }
        current = this.nodes[parentIdx];
      }
    }

    // Trimming from the root forward till the lowest common ancestor
    //
    // n0 ---> n1 ---> n2 ---> ...
    //                  |
    //                  +----> ...
    //
    // Would become:
    //
    // n2 ---> ...
    //  |
    //  +----> ...
    //
    // NOTE: it does not go beyond species level

    const rootIdx = 0;
    let currentIdx = rootIdx;
    const tmpGoodChildren = new Array<number>();
    while (visibleIndexes.has(this.nodes[currentIdx][NODE_IDX])) {
      if (this.nodes[currentIdx][NODE_RANK] === "species") {
        break;
      }

      tmpGoodChildren.length = 0;
      for (const childIdx of this.nodes[currentIdx][NODE_CHILDREN]) {
        if (visibleIndexes.has(childIdx)) {
          tmpGoodChildren.push(childIdx);
        }
      }
      if (tmpGoodChildren.length !== 1) {
        break;
      }
      visibleIndexes.delete(currentIdx);
      currentIdx = tmpGoodChildren[0];
    }

    // Make root (or lowest common ancestor) node first element
    visibleIndexes.delete(currentIdx);
    this.visibleIndexes.replace([currentIdx, ...visibleIndexes]);

    if (
      this.resultIndexes.size > MAX_NUM_OF_RESULTS ||
      this.visibleIndexes.size > MAX_NUM_OF_VISIBLE_NODES
    ) {
      // reducing the number of visible nodes using Breadth-First
      const queue: number[] = [currentIdx];
      const newVisibleIndexes = new Set<number>();
      while (queue.length > 0) {
        const idx = queue.shift()!;
        newVisibleIndexes.add(idx);
        if (newVisibleIndexes.size >= 10) {
          break;
        }

        for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
          if (this.visibleIndexes.has(childIdx)) {
            queue.push(childIdx);
          }
        }
      }

      // Also make sure that either all or none children are visible
      for (const idx of newVisibleIndexes) {
        let numOldVisible = 0;
        let numNewVisible = 0;
        for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
          if (this.visibleIndexes.has(this.nodes[childIdx][NODE_IDX])) {
            numOldVisible++;
          }
          if (newVisibleIndexes.has(this.nodes[childIdx][NODE_IDX])) {
            numNewVisible++;
          }
        }

        if (numOldVisible > 0 && numNewVisible > 0 && numNewVisible < numOldVisible) {
          for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
            if (
              this.nodes[childIdx][NODE_CHILDREN].length > 0 ||
              this.genomesStore.showUnsequencedInTree ||
              this.resultIndexes.has(childIdx)
            ) {
              newVisibleIndexes.add(childIdx);
            }
          }
        }
      }

      this.visibleIndexes.replace(newVisibleIndexes);
    }
  };

  @action private recalculateNumResults = (indexes: Set<number>) => {
    for (const idx of indexes) {
      this.numResultsWithinByIdx.delete(idx);
    }

    const calc = (idx: number): number => {
      if (this.numResultsWithinByIdx.has(idx)) {
        return this.numResultsWithinByIdx.get(idx)!;
      }

      let result = 0;
      for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
        if (this.resultIndexes.has(childIdx)) {
          result++;
        }
        result += calc(childIdx);
      }
      this.numResultsWithinByIdx.set(idx, result);
      return result;
    };

    for (const idx of indexes) {
      this.numResultsWithinByIdx.set(idx, calc(idx));
    }
  };

  hideRecursively = (idx: number) => {
    const iter = (idx: number) => {
      if (this.visibleIndexes.has(idx)) {
        this.visibleIndexes.delete(idx);
        for (const childIdx of this.nodes[idx][NODE_CHILDREN]) {
          iter(childIdx);
        }
      }
    };

    iter(idx);
  };

  @action private fetchInitialData = async () => {
    if (isServer) {
      // CatalogTreeStore is client only store
      return;
    }

    runInAction(() => {
      this.initialDataState = "fetching";
    });
    const responses = await Promise.all([
      this.api.get<CatalogTreeData>("/genome-portal-api/genomes/catalog-tree-data"),
      this.api.get<CatalogTreeUserData>("/genome-portal-api/genomes/catalog-tree-data/user-data"),
    ]);

    const res1 = responses[0];
    const res2 = responses[1];

    if (res1.error === undefined && res2.error === undefined) {
      runInAction(() => {
        this.initialDataState = "fetched";
        this.nodes.replace(res1.data.nodes);
        this.catalogNumberToTags = res2.data.tags;
        this.catalogNumberToUuid = res2.data.uuids;
        this.derivativeParents = res2.data.derivatives;
        this.recalculateNumWithUuidsWithinByIdx();
      });
    } else {
      runInAction(() => {
        this.initialDataState = "error";
      });
    }
  };

  private recalculateNumWithUuidsWithinByIdx = () => {
    this.numWithUuidsWithin = new Array(this.nodes.length).fill(0);

    for (const node of this.nodes) {
      if (
        node[NODE_CHILDREN].length === 0 &&
        node[NODE_CATALOG_NUMBER] &&
        this.catalogNumberToUuid[node[NODE_CATALOG_NUMBER]]
      ) {
        let currentIdx = node[NODE_PARENT];
        while (currentIdx !== null) {
          this.numWithUuidsWithin[currentIdx]++;
          currentIdx = this.nodes[currentIdx][NODE_PARENT];
        }
      }
    }
  };
}
